// ============================================================
// TMR Portal Admin — uma solicitação de cadastro (rota /empresas/solicitacoes/:applicationId) (28 §9)
// ============================================================
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@tmr/api-client';
import { Btn, StatusBadge } from '../../components/primitives';
import { Notice } from '../../components/Notice';
import { PageHeader } from '../../components/ui';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons, StrokeIcon } from '../../lib/icons';
import { maskCEP, maskCNPJ, maskCPF } from '../../lib/format';
import { displayPhone } from '../../lib/phone';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { useCompanyApplication, useReviewCompanyApplication } from '../../data/companyApplications/queries';
import {
  APPLICATION_STATUS_BADGE,
  applicationModeLabel,
  bankAccountTypeLabel,
  formatMoment,
  isOpen,
  revenueBandLabel,
  specialtyLabel,
} from '../../data/companyApplications/status';
import { companyFormFromApplication } from '../../data/companyApplications/prefill';
import type { CompanyApplicationDetail } from '../../data/companyApplications/contract';
import { NewCompanyModal } from '../companies/NewCompanyModal';
import { RejectApplicationDialog } from './RejectApplicationDialog';

/**
 * One application with everything the clinic typed, and what the team does with it: take it, reject it with a
 * reason, or approve it — which opens the Nova empresa wizard already filled in, to be completed and confirmed.
 */
export function CompanyApplicationDetailPage() {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const canRead = usePermission(PERMISSIONS.companyApplicationsRead);
  const query = useCompanyApplication(canRead ? applicationId : undefined);

  if (!canRead) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <div className="pa-card">
          <EmptyState
            message="Você não tem permissão para ver as solicitações de cadastro."
            hint="Peça o acesso a quem administra os papéis."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <button
        type="button"
        onClick={() => navigate('/empresas/solicitacoes')}
        style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--fg-muted)', fontFamily: 'var(--font-sans)', fontSize: 13.5, padding: 0 }}
      >
        <StrokeIcon d={Icons.chevronL} size={16} /> Solicitações
      </button>

      {query.isPending ? (
        <Skeleton rows={6} />
      ) : query.isError ? (
        query.error instanceof ApiError && query.error.isNotFound ? (
          <div className="pa-card">
            <EmptyState message="Solicitação não encontrada." />
          </div>
        ) : (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        )
      ) : (
        <ApplicationContent application={query.data} />
      )}
    </div>
  );
}

/** The loaded application — exported so the tests render it without a route. */
export function ApplicationContent({ application }: { application: CompanyApplicationDetail }) {
  const navigate = useNavigate();
  const canManage = usePermission(PERMISSIONS.companyApplicationsManage);
  const canOnboard = usePermission(PERMISSIONS.companyOnboard);
  const review = useReviewCompanyApplication(application.id);
  const [rejecting, setRejecting] = useState(false);
  const [approving, setApproving] = useState(false);
  const open = isOpen(application.status);
  const { clinic, contact, address, representative, bankAccount } = application;

  return (
    <>
      <PageHeader
        eyebrow={`Solicitação ${application.protocol} · ${applicationModeLabel(application.mode)}`}
        title={clinic.tradeName}
        desc={`CNPJ ${maskCNPJ(clinic.cnpj)} · recebida em ${formatMoment(application.submittedAt)}`}
        actions={
          open && canManage ? (
            <>
              <Btn variant="secondary" onClick={() => review.mutate()} disabled={review.isPending}>
                {review.isPending ? 'Assumindo…' : 'Assumir'}
              </Btn>
              <Btn variant="secondary" onClick={() => setRejecting(true)}>
                Reprovar
              </Btn>
              {canOnboard && (
                <Btn variant="primary" icon={Icons.check} onClick={() => setApproving(true)}>
                  Aprovar
                </Btn>
              )}
            </>
          ) : application.companyId ? (
            <Btn variant="secondary" icon={Icons.building} onClick={() => navigate(`/empresas/${application.companyId}`)}>
              Abrir empresa
            </Btn>
          ) : undefined
        }
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <StatusBadge status={APPLICATION_STATUS_BADGE[application.status] ?? application.status} />
        {application.reviewer && (
          <span style={{ fontSize: 13.5, color: 'var(--fg-muted)' }}>
            {reviewerSentence(application)}
          </span>
        )}
      </div>

      {review.isError && (
        <Notice tone="danger">
          {review.error instanceof ApiError ? review.error.detail : 'Não foi possível assumir a solicitação.'}
        </Notice>
      )}

      {open && canManage && !canOnboard && (
        <Notice tone="neutral">Aprovar também cria a empresa: peça a permissão de cadastrar empresas a quem administra os papéis.</Notice>
      )}

      {application.duplicateOfCompanyId && (
        <Notice tone="warning">
          Este CNPJ já é de uma empresa cadastrada.{' '}
          <a href={`/empresas/${application.duplicateOfCompanyId}`} onClick={(event) => {
            event.preventDefault();
            navigate(`/empresas/${application.duplicateOfCompanyId}`);
          }}>
            Abrir a empresa
          </a>
        </Notice>
      )}
      {!application.duplicateOfCompanyId && application.duplicateOfApplicationId && (
        <Notice tone="warning">
          Há outra solicitação aberta para este CNPJ.{' '}
          <a href={`/empresas/solicitacoes/${application.duplicateOfApplicationId}`} onClick={(event) => {
            event.preventDefault();
            navigate(`/empresas/solicitacoes/${application.duplicateOfApplicationId}`);
          }}>
            Abrir a outra solicitação
          </a>
        </Notice>
      )}

      {application.status === 'Rejected' && application.rejectionReason && (
        <Notice tone="neutral">
          <strong>Motivo da reprovação:</strong> <span style={{ whiteSpace: 'pre-wrap' }}>{application.rejectionReason}</span>
        </Notice>
      )}

      <Block title="Clínica">
        <Item label="Nome fantasia" value={clinic.tradeName} />
        <Item label="Razão social" value={clinic.legalName} />
        <Item label="CNPJ" value={maskCNPJ(clinic.cnpj)} />
        <Item label="Especialidade" value={specialtyLabel(clinic.specialty)} />
        <Item label="Faturamento mensal" value={revenueBandLabel(clinic.monthlyRevenueBand)} />
        <Item label="Instagram" value={clinic.instagram} />
        <Item label="Site" value={clinic.website} />
      </Block>

      <Block title="Contato">
        <Item label="Nome" value={contact.name} />
        <Item label="E-mail" value={contact.email} />
        <Item label="WhatsApp" value={displayPhone(contact.phone)} />
      </Block>

      <Block title="Endereço" empty={address ? null : 'A clínica não informou (cadastro rápido).'}>
        {address && (
          <>
            <Item label="CEP" value={maskCEP(address.zipCode)} />
            <Item label="Logradouro" value={`${address.street}, ${address.number}${address.complement ? ` — ${address.complement}` : ''}`} />
            <Item label="Bairro" value={address.district} />
            <Item label="Cidade" value={`${address.city}/${address.state}`} />
          </>
        )}
      </Block>

      <Block title="Representante legal" empty={representative ? null : 'A clínica não informou (cadastro rápido).'}>
        {representative && (
          <>
            <Item label="Nome" value={representative.name} />
            <Item label="CPF" value={maskCPF(representative.cpf)} />
            <Item label="E-mail" value={representative.email} />
            <Item label="Telefone" value={displayPhone(representative.phone)} />
          </>
        )}
      </Block>

      <Block title="Conta de recebimento" empty={bankAccount ? null : 'A clínica não informou.'}>
        {bankAccount && (
          <>
            <Item label="Banco" value={bankAccount.bankCode} />
            <Item label="Agência" value={bankAccount.branch} />
            <Item label="Conta" value={bankAccount.accountNumber} />
            <Item label="Tipo" value={bankAccountTypeLabel(bankAccount.accountType)} />
            <Item label="Chave PIX" value={bankAccount.pixKey} />
          </>
        )}
      </Block>

      <Block title="Consentimento">
        <Item label="Política de privacidade" value={`Versão ${application.consentPolicyVersion}`} />
        <Item label="Aceita em" value={formatMoment(application.consentedAt)} />
      </Block>

      {rejecting && <RejectApplicationDialog application={application} onClose={() => setRejecting(false)} />}

      {approving && (
        <NewCompanyModal
          initialForm={companyFormFromApplication(application)}
          approval={{ applicationId: application.id, protocol: application.protocol }}
          onClose={() => setApproving(false)}
          // Straight to the new Empresa, where its export to the legacy is followed; `replace` takes the wizard's
          // history entry with it.
          onCreated={(companyId) => navigate(`/empresas/${companyId}`, { replace: true })}
        />
      )}
    </>
  );
}

/** Who acted on the application, in the words of its status. */
function reviewerSentence(application: CompanyApplicationDetail): string {
  const name = application.reviewer?.name ?? '';
  const when = application.reviewedAt ? ` em ${formatMoment(application.reviewedAt)}` : '';
  switch (application.status) {
    case 'Approved':
      return `Aprovada por ${name}${when}`;
    case 'Rejected':
      return `Reprovada por ${name}${when}`;
    default:
      return `Em análise com ${name}${when}`;
  }
}

/** A titled card of label/value pairs, or a quiet line when the clinic left the block out. */
function Block({ title, empty, children }: { title: string; empty?: string | null; children: ReactNode }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18, margin: 0, color: 'var(--ink-900)' }}>{title}</h2>
      <div
        className="pa-card"
        style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px 24px' }}
      >
        {empty ? <p style={{ margin: 0, fontSize: 14, color: 'var(--fg-muted)' }}>{empty}</p> : children}
      </div>
    </section>
  );
}

/** One label/value pair; an empty value reads as a dash. */
function Item({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
      <span className="pa-eyebrow">{label}</span>
      <span style={{ fontSize: 14.5, color: 'var(--ink-900)', overflowWrap: 'anywhere' }}>{value?.trim() ? value : '—'}</span>
    </div>
  );
}
